import { useEffect, useRef } from 'react';
import { X, Download, FileText, Film, Image as ImageIcon } from 'lucide-react';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  fileType: string;
  fileUrl: string;
}

function getFileCategory(type: string): 'image' | 'video' | 'pdf' | 'unknown' {
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type === 'application/pdf') return 'pdf';
  return 'unknown';
}

export default function FilePreviewModal({
  isOpen,
  onClose,
  fileName,
  fileType,
  fileUrl,
}: FilePreviewModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const category = getFileCategory(fileType);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Visualizar: ${fileName}`}
    >
      <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-scale-in overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {category === 'image' && <ImageIcon size={16} className="text-brand-500 shrink-0" />}
            {category === 'video' && <Film size={16} className="text-brand-500 shrink-0" />}
            {category === 'pdf' && <FileText size={16} className="text-brand-500 shrink-0" />}
            <span className="text-sm font-medium text-slate-700 truncate">{fileName}</span>
          </div>
          <div className="flex items-center gap-2">
            {fileUrl && (
              <a
                href={fileUrl}
                download={fileName}
                className="p-1.5 rounded-lg text-slate-400 hover:text-brand-500 hover:bg-brand-50 transition-colors"
                aria-label="Baixar arquivo"
              >
                <Download size={18} />
              </a>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-slate-50 min-h-[300px]">
          {category === 'image' && fileUrl ? (
            <img
              src={fileUrl}
              alt={fileName}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
            />
          ) : category === 'video' && fileUrl ? (
            <video
              src={fileUrl}
              controls
              className="max-w-full max-h-[70vh] rounded-lg shadow-sm"
            >
              Seu navegador não suporta a reprodução de vídeo.
            </video>
          ) : category === 'pdf' && fileUrl ? (
            <iframe
              src={fileUrl}
              title={fileName}
              className="w-full h-[70vh] rounded-lg border border-slate-200"
            />
          ) : (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">
                Visualização não disponível para este tipo de arquivo
              </p>
              <p className="text-xs text-slate-400 mt-1">{fileType || 'Tipo desconhecido'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
